import { CreateWeddingState } from './';
import produce from 'immer';
import { ViewType, CreateEventJson, InputField, LocationType } from './types';
import { CurrentUser_me_eventUsers } from '@graphql/types';
import { addMonths, getDefaultTimezone } from './util';

export namespace handlers {
  export const handleFormInput = (field: string = '', value: string | Date = '', formError: boolean) =>
    produce<CreateWeddingState>(draft => {
      draft[field].value = value;
      if (draft[field].error) {
        draft[field].error = false;
      }
    });

  export const handleLocation = (
    value: string = '',
    lat: number | null,
    lng: number | null,
    placeId: string | null,
    tzid: string,
    formError?: boolean
  ) =>
    produce<CreateWeddingState>(draft => {
      draft.location.value = value;
      draft.location.lat = lat;
      draft.location.lng = lng;
      draft.location.placeId = placeId;
      draft.location.notApplicable = false;
      draft.location.tzid = tzid;
      if (draft.location.error) {
        draft.location.error = false;
      }
    });

  export const handleTimezoneChange = (method: string) =>
    produce<CreateWeddingState>(draft => {
      draft.location.tzMethod = method;
    });

  export const handleAddressChange = (address: string, tzid: string) =>
    produce<CreateWeddingState>(draft => {
      draft.location.value = address;
      draft.location.tzid = tzid;
    });

  export const handleDateInput = (value: Date) =>
    produce<CreateWeddingState>(draft => {
      draft.eventDate = {
        value,
        changed: true,
        notApplicable: false
      };
    });

  export const handleShowView = () =>
    produce<CreateWeddingState>(draft => {
      draft.views = { ...draft.views, [draft.currentView]: true };
    });

  export const handleNotApplicable = (field: string) =>
    produce<CreateWeddingState>(draft => {
      draft[field].notApplicable = !draft[field].notApplicable;
      draft[field].value = null;
      draft[field].changed = false;
      if (field === 'location') {
        draft[field].lat = null;
        draft[field].lng = null;
        draft[field].placeId = null;
        draft[field].value = '';
      }
    });

  export const hydrateStateFromLocalStorage = (eventData: CreateEventJson) =>
    produce<CreateWeddingState>(draft => {
      draft.ownerFirstName.value = eventData.ownerFirstName;
      draft.ownerLastName.value = eventData.ownerLastName;
      draft.partnerFirstName.value = eventData.fianceeFirstName;
      draft.partnerLastName.value = eventData.fianceeLastName;
      draft.location = {
        value: eventData.place,
        error: false,
        lng: eventData.lng,
        lat: eventData.lat,
        placeId: eventData.placeId,
        notApplicable: false,
        tzid: eventData.tzid,
        tzMethod: eventData.tzMethod,
        errorText: 'Please enter a Location'
      };
      if (eventData.date) {
        draft.eventDate = {
          value: new Date(eventData.date),
          changed: true,
          notApplicable: false
        };
      } else {
        draft.eventDate = {
          value: addMonths(new Date(), 9),
          changed: false,
          notApplicable: false
        };
      }
    });

  export const handleClearForm = () => {
    const tzdata = getDefaultTimezone();
    return produce<CreateWeddingState>(draft => {
      draft.ownerFirstName.value = '';
      draft.ownerFirstName.error = false;
      draft.ownerLastName.value = '';
      draft.ownerLastName.error = false;
      draft.partnerFirstName.value = '';
      draft.partnerFirstName.error = false;
      draft.partnerLastName.value = '';
      draft.partnerLastName.error = false;

      draft.location = {
        value: '',
        error: false,
        errorText: draft.location.errorText,
        lng: null,
        lat: null,
        placeId: null,
        notApplicable: false,
        tzid: tzdata.tzid,
        tzMethod: tzdata.tzMethod
      };
      draft.eventDate = {
        value: addMonths(new Date(), 9),
        changed: false,
        notApplicable: false
      };
    });
  };

  export const handleGoToView = (view: ViewType, result?: CurrentUser_me_eventUsers) => {
    return produce<CreateWeddingState>(draft => {
      if (view === 'existingAccount' && result) {
        draft.currentView = 'existingAccount';
        draft.views = { createForm: false, existingAccount: false, authenticate: false };
        if (result.eventV0918) {
          draft.eventUrl = result.eventV0918.website;
        }
      } else {
        draft.views = { createForm: false, existingAccount: false, authenticate: false };
        draft.currentView = view;
      }
    });
  };

  export const handleSetErrors = (
    ownerFirstName: InputField,
    ownerLastName: InputField,
    partnerFirstName: InputField,
    partnerLastName: InputField,
    location: LocationType
  ) => {
    const hasError = (input: InputField | LocationType) => input.value === '';
    return produce<CreateWeddingState>(draft => {
      draft.ownerFirstName.error = hasError(ownerFirstName);
      draft.ownerLastName.error = hasError(ownerLastName);
      draft.partnerFirstName.error = hasError(partnerFirstName);
      draft.partnerLastName.error = hasError(partnerLastName);
      draft.location.error = hasError(location);
    });
  };
}
